import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { UserAccountContext } from '@/api/v1/auth/get-user-account-context'
import type { UserAccountContextItem } from '@/api/v1/auth/get-user-account-contexts'
import { toAppLocale } from '@/lib/intl/locale-bcp47'
import { LOCAL_STORAGE_KEYS } from '@/lib/local-storage-keys'
import { logger } from '@/lib/logger'
import {
  applyWhiteLabelTheme,
  updateFavicon,
  updateManifest,
} from '@/lib/white-label'

import { useLanguageStore } from './language'
import { useThemeStore } from './theme'
import { useWhiteLabelStore } from './white-label'

interface ContextStore {
  // List of available user accounts
  availableUserAccounts: Array<UserAccountContextItem>
  setAvailableUserAccounts: (
    userAccounts: Array<UserAccountContextItem>,
  ) => void

  // Currently selected user account (userAccountId)
  selectedUserAccountId: string | null
  setSelectedUserAccountId: (userAccountId: string) => void

  // Full context of the selected user account
  userAccountContext: UserAccountContext | null
  setUserAccountContext: (context: UserAccountContext) => void

  // Clear all data (used on logout)
  clearContextData: () => void

  // Hydration state
  _hasHydrated: boolean
  _setHasHydrated: (value: boolean) => void
}

export const useContextStore = create<ContextStore>()(
  // @ts-ignore - Known issue with Zustand 5.x and TypeScript strict mode with persist middleware
  persist(
    (set) => ({
      availableUserAccounts: [],
      selectedUserAccountId: null,
      userAccountContext: null,
      _hasHydrated: false,

      setAvailableUserAccounts: (accounts) => {
        if (!Array.isArray(accounts)) {
          logger.warning('Ignored invalid user account list', {
            receivedType: typeof accounts,
          })
          return
        }

        set({ availableUserAccounts: accounts })

        // If there's only one account, select it automatically
        if (accounts.length === 1) {
          set({ selectedUserAccountId: accounts[0].userAccountId })
        }
      },

      setSelectedUserAccountId: (userAccountId) => {
        set({ selectedUserAccountId: userAccountId })
      },

      setUserAccountContext: (context) => {
        set({ userAccountContext: context })
      },

      _setHasHydrated: (value) => {
        set({ _hasHydrated: value })
      },

      clearContextData: () => {
        set({
          availableUserAccounts: [],
          selectedUserAccountId: null,
          userAccountContext: null,
        })

        // Clear white-label settings on logout and revert to Signater branding
        if (typeof window !== 'undefined') {
          useWhiteLabelStore.getState().setWhiteLabel({
            isWhiteLabel: false,
            companyName: 'Signater',
            theme: null,
            rectangleLightLogoUrl: null,
            rectangleDarkLogoUrl: null,
            squareLightLogoUrl: null,
            squareDarkLogoUrl: null,
            faviconUrl: null,
            googleAppClientId: null,
            microsoftAppClientId: null,
            turnstileSiteKey: null, // Not returned by this endpoint
            hasSupportChatDisabled: false,
            isSsoAvailable: false, // Not returned by this endpoint; post-auth, the login gate is moot
          })

          // Revert to default Signater branding (Blue theme)
          // Title is managed by usePageTitle hook
          applyWhiteLabelTheme(null)
          updateFavicon(null)
          updateManifest('Signater', null)
        }

        // Note: Theme and language are NOT cleared on logout
        // They persist across sessions as per user preference
      },
    }),
    {
      name: LOCAL_STORAGE_KEYS.CONTEXT,
      onRehydrateStorage: () => (state) => {
        if (state) {
          state._setHasHydrated(true)
        }
      },
    },
  ),
)

/**
 * Subscribe to accountContext changes to automatically sync language and theme
 *
 * This ensures that whenever the account context is updated (login, account switch,
 * or periodic refresh), the user's language and theme preferences from the API
 * are applied to the local stores.
 *
 * This creates a one-way sync: API -> Local stores
 * When user changes preferences in the UI, the hooks will update both the API and local stores.
 */
let previousAccountContext: UserAccountContext | null = null
useContextStore.subscribe((state) => {
  const accountContext = state.userAccountContext
  if (accountContext && accountContext !== previousAccountContext) {
    const language = accountContext.userAccountInformation.language

    // Sync language from API to local store. An unmapped value keeps whatever
    // the user already had — writing `undefined` into the store used to leave
    // every locale lookup downstream without a match.
    const locale = toAppLocale(language)
    if (locale) {
      useLanguageStore.getState().setLocale(locale)
    }

    // Sync theme from API to local store
    const apiTheme = accountContext.userAccountInformation.theme
    if (apiTheme === 'Dark') {
      useThemeStore.getState().setTheme('dark')
    } else {
      useThemeStore.getState().setTheme('light')
    }

    previousAccountContext = accountContext
  }
})

/**
 * Subscribe to accountContext changes to automatically sync white-label settings
 *
 * This ensures that whenever the account context is updated (login, account switch,
 * or periodic refresh), the white-label configuration from the account is applied.
 *
 * This handles scenarios where:
 * - User is on a white-label domain but accesses a non-white-label account
 * - User is on Signater domain but accesses a white-label account
 * - User switches between accounts with different branding
 */
if (typeof window !== 'undefined') {
  let previousWhiteLabelContext: UserAccountContext | null = null
  useContextStore.subscribe((state) => {
    const accountContext = state.userAccountContext
    if (accountContext !== previousWhiteLabelContext) {
      if (
        accountContext?.hasWhiteLabelEnabled &&
        accountContext.whiteLabelAppInformation
      ) {
        // Account has white-label configuration - apply it
        const wl = accountContext.whiteLabelAppInformation

        useWhiteLabelStore.getState().setWhiteLabel({
          isWhiteLabel: true,
          companyName: wl.companyName || 'Signater',
          theme: wl.theme,
          rectangleLightLogoUrl: wl.rectangleLightLogoUrl,
          rectangleDarkLogoUrl: wl.rectangleDarkLogoUrl,
          squareLightLogoUrl: wl.squareLightLogoUrl,
          squareDarkLogoUrl: wl.squareDarkLogoUrl,
          faviconUrl: wl.faviconUrl,
          googleAppClientId: null, // Not returned by this endpoint
          microsoftAppClientId: null,
          turnstileSiteKey: null, // Not returned by this endpoint
          hasSupportChatDisabled:
            accountContext.accountInformation.hasSupportChatDisabled,
          isSsoAvailable: false, // Not returned by this endpoint; post-auth, the login gate is moot
        })

        // Apply white-label immediately (title is managed by usePageTitle hook)
        applyWhiteLabelTheme(wl.theme)
        updateFavicon(wl.faviconUrl)
        updateManifest(wl.companyName || 'Signater', wl.faviconUrl)
      } else if (accountContext) {
        // Account doesn't have white-label - revert to Signater branding
        // This is important when switching from a white-label account to a regular one
        useWhiteLabelStore.getState().setWhiteLabel({
          isWhiteLabel: false,
          companyName: 'Signater',
          theme: null,
          rectangleLightLogoUrl: null,
          rectangleDarkLogoUrl: null,
          squareLightLogoUrl: null,
          squareDarkLogoUrl: null,
          faviconUrl: null,
          googleAppClientId: null,
          microsoftAppClientId: null,
          turnstileSiteKey: null, // Not returned by this endpoint
          hasSupportChatDisabled:
            accountContext.accountInformation.hasSupportChatDisabled,
          isSsoAvailable: false, // Not returned by this endpoint; post-auth, the login gate is moot
        })

        // Revert to default Signater branding (Blue theme)
        // Title is managed by usePageTitle hook
        applyWhiteLabelTheme(null)
        updateFavicon(null)
        updateManifest('Signater', null)
      }
      previousWhiteLabelContext = accountContext
    }
  })
}

/**
 * Subscribe to accountContext changes to automatically sync Sentry user context
 *
 * This ensures that whenever the account context is updated (login, account switch,
 * or periodic refresh), the user information is sent to Sentry for error tracking
 * and debugging purposes.
 */
if (typeof window !== 'undefined') {
  let previousSentryContext: UserAccountContext | null = null
  useContextStore.subscribe((state) => {
    const accountContext = state.userAccountContext

    // Track if this is the first time we're setting context
    const isFirstLogin =
      previousSentryContext === null && accountContext !== null
    const hasUserSwitched =
      previousSentryContext &&
      accountContext &&
      previousSentryContext.userAccountInformation.id !==
        accountContext.userAccountInformation.id

    if (accountContext) {
      const userInfo = accountContext.userAccountInformation
      const accountInfo = accountContext.accountInformation

      // Set user information in Sentry
      logger.setUser(userInfo.id, userInfo.email, userInfo.name, userInfo.id)

      // Add additional context tags
      logger.setTags({
        accountId: accountInfo.id,
        accountName: accountInfo.name,
        userRole: userInfo.role,
        featureType: accountContext.balanceInformation.featureType,
      })

      // Log only on initial login (not on refreshes)
      if (isFirstLogin) {
        logger.info('User logged in successfully', {
          userId: userInfo.id,
          email: userInfo.email,
          accountId: accountInfo.id,
          accountName: accountInfo.name,
        })
      } else if (hasUserSwitched) {
        // Log on user switch
        logger.info('User switched account context', {
          userId: userInfo.id,
          email: userInfo.email,
          accountId: accountInfo.id,
          accountName: accountInfo.name,
        })
      }
    } else if (previousSentryContext) {
      // Clear user from Sentry when context is cleared (logout)
      logger.clearUser()
      logger.info('User logged out')
    }

    previousSentryContext = accountContext
  })
}
